import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { CFR_CATEGORIES } from "../../constants/cfrCategories";

const CFR_LABEL_BY_VALUE = Object.fromEntries(
  CFR_CATEGORIES.map(c => [c.value, c.label])
);

function formatOutcomeLabel(unit) {
  if (!unit) return "documented outcomes";
  return String(unit).replace(/_/g, " ");
}

function countTruthyMapItems(obj) {
  if (!obj || typeof obj !== "object") return 0;
  return Object.values(obj).filter(Boolean).length;
}

function sumOutcomeValues(outcomes) {
  if (!outcomes || typeof outcomes !== "object") return 0;
  return Object.values(outcomes).reduce((sum, amount) => {
    return sum + Number(amount || 0);
  }, 0);
}

export default function CommunityImpactDashboard() {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommunityImpactSummary();
  }, []);

  async function fetchCommunityImpactSummary() {
    try {
      setLoading(true);

      const snap = await getDocs(collection(db, "communityImpactSummary"));

      const rows = snap.docs.map(d => {
        const data = d.data();
        const value = data.value || d.id;
const outcomes = data.outcomes && typeof data.outcomes === "object"
  ? { ...data.outcomes }
  : {};

const organizationIds = data.organizationIds && typeof data.organizationIds === "object"
  ? { ...data.organizationIds }
  : {};

Object.entries(data).forEach(([key, val]) => {
  if (key.startsWith("outcomes.")) {
    const outcomeName = key.replace("outcomes.", "");
    outcomes[outcomeName] = Number(val || 0);
  }

  if (key.startsWith("organizationIds.")) {
    const orgId = key.replace("organizationIds.", "");
    organizationIds[orgId] = val;
  }
});

return {
  id: d.id,
  value,
  label: CFR_LABEL_BY_VALUE[value] || value,
  fundedCommitments: Number(data.fundedCommitments || 0),
  deliveries: Number(data.deliveries || 0),
  outcomes,
  organizationIds,
  updatedAt: data.updatedAt || null
};
      });

      setSummaries(rows);
    } catch (err) {
      console.error("Error loading community impact dashboard:", err);
    } finally {
      setLoading(false);
    }
  }

  const impactByTopic = useMemo(() => {
    const byValue = Object.fromEntries(summaries.map(s => [s.value, s]));

    return CFR_CATEGORIES.map(category => {
      const summary = byValue[category.value] || {};
      const outcomes = summary.outcomes || {};
      const organizationIds = summary.organizationIds || {};

      return {
        value: category.value,
        topic: category.label,
        fundedCommitments: Number(summary.fundedCommitments || 0),
        deliveries: Number(summary.deliveries || 0),
        organizationCount: countTruthyMapItems(organizationIds),
        outcomeTotal: sumOutcomeValues(outcomes),
        topOutcomes: Object.entries(outcomes)
          .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
          .slice(0, 3)
      };
    }).sort((a, b) => {
      if (b.fundedCommitments !== a.fundedCommitments) return b.fundedCommitments - a.fundedCommitments;
      if (b.deliveries !== a.deliveries) return b.deliveries - a.deliveries;
      return CFR_CATEGORIES.findIndex(c => c.value === a.value) -
        CFR_CATEGORIES.findIndex(c => c.value === b.value);
    });
  }, [summaries]);

  const totals = useMemo(() => {
    const orgs = new Set();
    let fundedCommitments = 0;
    let deliveries = 0;
    let documentedOutcomes = 0;

    summaries.forEach(s => {
      fundedCommitments += Number(s.fundedCommitments || 0);
      deliveries += Number(s.deliveries || 0);
      Object.keys(s.organizationIds || {}).forEach(orgId => {
        if (s.organizationIds[orgId]) orgs.add(orgId);
      });
      documentedOutcomes += sumOutcomeValues(s.outcomes);
    });

    return {
      fundedCommitments,
      organizations: orgs.size,
      deliveries,
      documentedOutcomes
    };
  }, [summaries]);

  const activeTopics = impactByTopic.filter(t => t.fundedCommitments > 0 || t.deliveries > 0);
  const inactiveTopics = impactByTopic.filter(t => t.fundedCommitments === 0 && t.deliveries === 0);
  const maxDeliveries = Math.max(...impactByTopic.map(t => t.deliveries), 1);

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", color: "#102844" }}>
      <header style={{
        background: "white",
        borderBottom: "1px solid #e5e7eb",
        padding: "1rem 2rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <div
          onClick={() => navigate("/")}
          style={{ fontWeight: 800, fontSize: "1.2rem", cursor: "pointer", color: "#1e3a5f" }}
        >
          somebody.gives
        </div>

        <button
          onClick={() => navigate("/give/browse-public")}
          style={{
            border: "none",
            background: "#1e3a5f",
            color: "white",
            padding: "0.7rem 1rem",
            borderRadius: "8px",
            fontWeight: 700,
            cursor: "pointer"
          }}
        >
          Browse needs →
        </button>
      </header>

      <main style={{ maxWidth: "1120px", margin: "0 auto", padding: "2.5rem 1.5rem" }}>
        <section style={{
          background: "linear-gradient(135deg, #1e3a5f, #2d6a4f)",
          color: "white",
          padding: "2.75rem",
          borderRadius: "24px",
          marginBottom: "2rem",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)"
        }}>
          <div style={{ fontSize: "0.85rem", opacity: 0.9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Springfield Community Impact
          </div>

          <h1 style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)", lineHeight: 1.05, margin: "0.65rem 0 1rem" }}>
            Local needs. Real deliveries. Measurable impact.
          </h1>

          <p style={{ maxWidth: "760px", lineHeight: 1.65, opacity: 0.92, fontSize: "1.05rem" }}>
            Every number below represents supplies delivered to local nonprofits and confirmed received.
            Needs are organized using Springfield Community Focus Report priorities.
          </p>
        </section>

        {loading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "#64748b" }}>
            Loading community impact...
          </div>
        ) : (
          <>
            <section style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "1rem",
              marginBottom: "2.25rem"
            }}>
              <StatCard number={totals.fundedCommitments} label="Funded commitments" />
              <StatCard number={totals.organizations} label="Organizations supported" />
              <StatCard number={totals.deliveries} label="Deliveries confirmed" />
              <StatCard number={totals.documentedOutcomes} label="Documented outcomes" />
            </section>

            <div style={{ marginBottom: "1rem" }}>
              <h2 style={{ margin: 0, color: "#1e3a5f", fontSize: "1.45rem" }}>
                Funded Community Priorities
              </h2>
              <p style={{ color: "#64748b", marginTop: "0.35rem" }}>
                Areas where confirmed deliveries have already been recorded.
              </p>
            </div>

            <section style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1rem"
            }}>
              {activeTopics.length > 0 ? activeTopics.map(topic => (
                <TopicCard key={topic.value} topic={topic} maxDeliveries={maxDeliveries} />
              )) : (
                <div style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "16px",
                  padding: "2rem",
                  color: "#64748b"
                }}>
                  No confirmed deliveries have been summarized yet.
                </div>
              )}
            </section>

            {inactiveTopics.length > 0 && (
              <>
                <div style={{ marginTop: "3rem", marginBottom: "1rem" }}>
                  <h2 style={{ margin: 0, color: "#1e3a5f", fontSize: "1.25rem" }}>
                    Community Priorities Not Yet Funded
                  </h2>
                  <p style={{ color: "#64748b", marginTop: "0.35rem" }}>
                    These areas are part of the Community Focus Report framework but do not yet have confirmed deliveries through SomebodyGives.
                  </p>
                </div>

                <section style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "0.75rem"
                }}>
                  {inactiveTopics.map(topic => (
                    <div key={topic.value} style={{
                      background: "#f1f5f9",
                      border: "1px solid #e5e7eb",
                      borderRadius: "14px",
                      padding: "1rem",
                      color: "#64748b",
                      fontWeight: 700
                    }}>
                      {topic.topic}
                    </div>
                  ))}
                </section>
              </>
            )}

            <div style={{
              marginTop: "3rem",
              background: "#eef6f1",
              border: "1px solid #d7eadf",
              borderRadius: "16px",
              padding: "1rem 1.25rem",
              color: "#315b45",
              lineHeight: 1.55
            }}>
              Impact is updated when local nonprofits confirm deliveries have been received.
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ number, label }) {
  return (
    <div style={{
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: "18px",
      padding: "1.5rem",
      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)"
    }}>
      <div style={{ fontSize: "2.6rem", fontWeight: 900, color: "#065f46", lineHeight: 1 }}>
        {Number(number || 0).toLocaleString()}
      </div>
      <div style={{ color: "#475569", fontSize: "0.92rem", fontWeight: 700, marginTop: "0.55rem" }}>
        {label}
      </div>
    </div>
  );
}

function TopicCard({ topic, maxDeliveries }) {
  const primaryOutcome = topic.topOutcomes[0];
  const barWidth = topic.deliveries > 0
    ? Math.max(8, Math.round((topic.deliveries / maxDeliveries) * 100))
    : 0;

  return (
    <article style={{
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: "18px",
      padding: "1.35rem",
      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "flex-start" }}>
        <h3 style={{ margin: 0, fontSize: "1.18rem", color: "#1e3a5f", lineHeight: 1.25 }}>
          {topic.topic}
        </h3>
        <span style={{
          background: "#ecfdf5",
          color: "#166534",
          borderRadius: "999px",
          padding: "0.25rem 0.6rem",
          fontSize: "0.75rem",
          fontWeight: 800,
          whiteSpace: "nowrap"
        }}>
          Active
        </span>
      </div>

      <div style={{ marginTop: "0.85rem", color: "#334155", fontSize: "0.9rem", lineHeight: 1.7 }}>
        <strong>{topic.fundedCommitments}</strong> funded commitment{topic.fundedCommitments === 1 ? "" : "s"}
        {" · "}
        <strong>{topic.organizationCount}</strong> org{topic.organizationCount === 1 ? "" : "s"}
        {" · "}
        <strong>{topic.deliveries}</strong> deliver{topic.deliveries === 1 ? "y" : "ies"}
      </div>

      <div style={{
        marginTop: "1rem",
        height: "8px",
        borderRadius: "999px",
        background: "#e5e7eb",
        overflow: "hidden"
      }}>
        <div style={{
          width: `${barWidth}%`,
          height: "100%",
          background: "#22c55e"
        }} />
      </div>

      {primaryOutcome ? (
        <div style={{
          marginTop: "1rem",
          background: "#f0fdf4",
          border: "1px solid #dcfce7",
          borderRadius: "14px",
          padding: "1rem"
        }}>
          <div style={{ fontSize: "2rem", fontWeight: 900, color: "#166534", lineHeight: 1 }}>
            {Number(primaryOutcome[1] || 0).toLocaleString()}
          </div>
          <div style={{ color: "#166534", fontSize: "0.92rem", marginTop: "0.35rem", lineHeight: 1.35 }}>
            {formatOutcomeLabel(primaryOutcome[0])}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: "1rem", color: "#64748b", fontSize: "0.88rem" }}>
          Funded activity recorded.
        </div>
      )}
    </article>
  );
}